// Conditional display ("branching") — the frontend mirror of
// survey-runtime.service/app/services/survey_branching.py::compute_visible.
// Both sides MUST implement identical semantics: the server only requires /
// stores answers for questions this computation makes visible.
//
// Rules shape: { rules: { [optionKey]: [target, ...] } } — choosing an option
// reveals the listed LATER targets. A target is either a question_key or a
// whole section, namespaced as "sec:<position>" (the section's positional
// index in survey.sections).
//  * a question named in ANY rule (of any question) is CONDITIONAL: hidden
//    unless some visible question is answered with an option that lists it
//  * a section named in any rule is CONDITIONAL the same way: its tab and
//    every question inside it are hidden until an option reveals it
//  * everything else is unconditional and always visible
//  * questions keep their authored order; revealing never reorders anything
//  * rules on a hidden question never fire (conditions cascade); targets are
//    forward-only, so one ordered pass resolves visibility
//
// Legacy rows may still carry string targets / "default" / "__END__" from the
// old skip-logic walk: strings coerce to one-element lists, the sentinels are
// ignored.

export const BRANCH_END = "__END__";
export const SECTION_TARGET_PREFIX = "sec:";

export function sectionTarget(index) {
  return `${SECTION_TARGET_PREFIX}${index}`;
}

// Returns the section position for a "sec:<n>" target, else null.
export function parseSectionTarget(target) {
  if (typeof target !== "string" || !target.startsWith(SECTION_TARGET_PREFIX)) return null;
  const n = Number(target.slice(SECTION_TARGET_PREFIX.length));
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export function flattenQuestions(survey) {
  return (survey?.sections || []).flatMap((s) => s.questions || []);
}

function coerceTargets(value) {
  if (typeof value === "string") return value === BRANCH_END ? [] : [value];
  if (Array.isArray(value)) {
    return value.filter((t) => typeof t === "string" && t !== BRANCH_END);
  }
  return [];
}

// Single ordered pass shared by computeVisiblePath / computeVisibleSections.
// Returns { visible: Set<question_key>, visibleSections: Set<sectionIndex> }.
function walkVisibility(survey, answers) {
  const sections = survey?.sections || [];
  // [{ q, sectionIndex }] in authored order.
  const flat = sections.flatMap((s, si) => (s.questions || []).map((q) => ({ q, sectionIndex: si })));

  const conditional = new Set();
  const conditionalSections = new Set();
  for (const { q } of flat) {
    for (const value of Object.values(q.branching?.rules || {})) {
      for (const t of coerceTargets(value)) {
        const secIdx = parseSectionTarget(t);
        if (secIdx !== null) conditionalSections.add(secIdx);
        else conditional.add(t);
      }
    }
  }

  const revealed = new Set();
  const revealedSections = new Set();
  const visible = new Set();
  for (const { q, sectionIndex } of flat) {
    if (conditionalSections.has(sectionIndex) && !revealedSections.has(sectionIndex)) {
      continue; // whole section hidden: nothing revealed it (yet)
    }
    const key = q.question_key;
    if (conditional.has(key) && !revealed.has(key)) continue; // hidden
    visible.add(key);
    const rules = q.branching?.rules || {};
    const v = answers?.[key];
    const chosen = typeof v === "string" && v
      ? v
      : (v && typeof v === "object" && !Array.isArray(v) && typeof v.value === "string" && v.value)
        ? v.value
        : null;
    if (chosen && chosen in rules) {
      for (const t of coerceTargets(rules[chosen])) {
        const secIdx = parseSectionTarget(t);
        if (secIdx !== null) revealedSections.add(secIdx);
        else revealed.add(t);
      }
    }
  }

  const visibleSections = new Set();
  sections.forEach((_, si) => {
    if (!conditionalSections.has(si) || revealedSections.has(si)) visibleSections.add(si);
  });
  return { visible, visibleSections };
}

// Returns a Set of visible question_keys given the current answers map.
// Single/dropdown answers are stored as the chosen option_key string.
export function computeVisiblePath(survey, answers) {
  return walkVisibility(survey, answers).visible;
}

// Returns a Set of visible section indices (positions in survey.sections).
// Sections never named as a "sec:<n>" target are always visible.
export function computeVisibleSections(survey, answers) {
  return walkVisibility(survey, answers).visibleSections;
}

// True when any question in the survey carries display conditions — used to
// skip the computation entirely for ordinary surveys.
export function surveyHasBranching(survey) {
  return flattenQuestions(survey).some((q) => q.branching);
}
